import { AttachmentMedia, cn } from "@pdfking/components"
import { UploadIcon } from "lucide-react"

import { useTranslationTyped } from "../i18n/useTranslation"

/**
 * The one way to bring files in: a dashed card that opens the file picker.
 * Used under the file rows (see FileRows) and as the viewer's empty state, so
 * "add a file" looks and behaves the same in both places instead of one being a
 * button and the other a sentence.
 *
 * It carries no drop handling of its own even though it says "drag and drop":
 * a drop anywhere on the window is already caught once, at the root (see the
 * host's NativeDrop), which is what makes the whole surface a target rather
 * than this one card.
 */
export function UploadCard({
  className = "",
  on_files,
}: {
  className?: string
  on_files: ((files: File[]) => void) | undefined
}) {
  const actions = useTranslationTyped("actions")
  return (
    <label
      className={cn(
        "flex cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed bg-card p-2 transition-colors hover:bg-accent",
        className
      )}
    >
      <AttachmentMedia>
        <UploadIcon />
      </AttachmentMedia>
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium">{actions.DROP_FILES()}</span>
        <span className="truncate text-xs text-muted-foreground">{actions.DROP_OR_CLICK()}</span>
      </div>
      <input
        accept=".pdf,application/pdf"
        className="hidden"
        multiple
        onChange={(e: Event) => {
          const input = e.target as HTMLInputElement
          const files = [...(input.files ?? [])]
          if (files.length) on_files?.(files)
          // Clear it, or picking the same file twice in a row fires no `change`.
          input.value = ""
        }}
        type="file"
      />
    </label>
  )
}
